// src/components/AuthModal.tsx
import { useState, FormEvent } from 'react'
import { supabase } from '~/supabaseClient'
import { Dialog } from '~/components/ui/Dialog'
import { Label } from '~/components/ui/Label'
import Alert from '~/components/ui/Alert'

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      setLoading(true)
      setErrorMessage('')

      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: window.location.href,
        },
      })

      if (error) throw error

      setEmailSent(true)
      console.log('✅ Magic link sent successfully')
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      }
      console.error('❌ Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="max-w-[95vw] sm:max-w-md p-4 sm:p-8" showCloseButton={true}>
        {/* Header */}
        <Dialog.Title className="mb-4">
          Sign In
        </Dialog.Title>

        {/* Explanation */}
        <Dialog.Description className="mb-6 text-gray-300">
          Only registered users can select which CC params or notes will be scrambled for connected MIDI devices.
          Please sign in to continue.
        </Dialog.Description>

        {/* Beta Access Notice */}
        <div className="mb-6 p-4 bg-yomidi-orange-neon/10 border border-yomidi-orange-neon rounded-lg">
          <p className="text-sm font-semibold text-yomidi-orange-neon mb-2">
            Beta Access Required
          </p>
          <p className="text-xs text-yomidi-orange-vivid mb-1">
            We're currently in private beta. You will need an invitation to sign in.
          </p>
          <p className="text-xs text-yomidi-orange-vivid">
            To request access, please email{' '}
            <a href="mailto:hello@yomidi.com" className="underline hover:text-yomidi-orange-light">
              hello@yomidi.com
            </a>
          </p>
        </div>

        {/* Auth Form */}
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <Label htmlFor="modal-email" className="block mb-2 text-gray-300">
              Email address
            </Label>
            <input
              type="email"
              id="modal-email"
              className="p-3 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 px-5 text-white bg-purple-700 hover:bg-purple-600 focus:ring-4 focus:outline-none focus:ring-purple-800 font-medium rounded-lg text-sm text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? <span>Sending...</span> : <span>Send magic link</span>}
          </button>

          {emailSent && (
            <Alert variant="success" className="mt-4 rounded-lg">
              Check your email for the magic link!
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="error" className="mt-4 rounded-lg">
              {errorMessage}
            </Alert>
          )}
        </form>
      </Dialog.Content>
    </Dialog>
  )
}
